import { useQuery, UseQueryResult } from '@tanstack/react-query'
import {
  getResurrectionBySlug,
  ResurrectionContent
} from '../../../services/resurrectionContentful'

/**
 * React-Query hook that fetches resurrection landing data from Contentful.
 *
 * @param slug The slug taken from the URL (`/resurrection/:slug`). If undefined
 *             the query is disabled and returns `undefined` data.
 */
export function useContentfulResurrection(
  slug: string | undefined
): UseQueryResult<ResurrectionContent, Error> {
  const result = useQuery({
    queryKey: ['resurrectionLanding', slug],
    queryFn: async () => {
      if (!slug) throw new Error('Missing slug')

      const data = await getResurrectionBySlug(slug)
      return data
    },
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000, // 5 min cache
    retry: 1, // Only retry once to avoid infinite loops
    retryDelay: 1000 // Wait 1 second before retrying
  })

  // Return a consistent non-loading state when query is disabled
  if (!slug) {
    return ({
      ...result,
      isLoading: false,
      isPending: false,
      isFetching: false,
      data: undefined,
      error: undefined,
      isError: false,
      isLoadingError: false,
      isRefetchError: false,
      isSuccess: false,
      isPlaceholderData: false
    } as unknown) as UseQueryResult<ResurrectionContent, Error>
  }

  return result
}
